/**
 * OrderModelViewService
 * Service for Order ModelView endpoints
 * Route: api/orders
 * Controller: OrderModelViewController.cs
 */
import { BaseService } from '../base/BaseService';
/**
 * OrderModelViewService
 * Handles order model view queries (enriched data)
 * Maps to /api/orders/{view-endpoints}
 */
export class OrderModelViewService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? OrderModelViewService.DEFAULT_SERVICE_PATH,
            basePath: OrderModelViewService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== SINGLE VIEW ====================
    /**
     * Get order model view by ID
     * GET /api/orders/view/{id}
     */
    async getView(id) {
        const response = await this.getViewRaw(id);
        if (!response.data) {
            throw new Error(`Order view with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get order model view by ID (raw)
     */
    getViewRaw(id) {
        const endpoint = this.getEndpoint('view', `view/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== ALL VIEWS ====================
    /**
     * Get all order model views
     * GET /api/orders/view
     */
    async getAllViews() {
        const response = await this.getAllViewsRaw();
        return response.data ?? [];
    }
    /**
     * Get all order model views (raw)
     */
    getAllViewsRaw() {
        const endpoint = this.getEndpoint('view-all', 'view');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== VIEWS BY LOCATION ====================
    /**
     * Get order views by location
     * GET /api/orders/location/{locationId}/view
     */
    async getViewsByLocation(locationId) {
        const response = await this.getViewsByLocationRaw(locationId);
        return response.data ?? [];
    }
    /**
     * Get order views by location (raw)
     */
    getViewsByLocationRaw(locationId) {
        const endpoint = this.getEndpoint('views-by-location', `location/${locationId}/view`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== VIEWS BY STATUS ====================
    /**
     * Get order views by status
     * GET /api/orders/status/{statusId}/view
     */
    async getViewsByStatus(statusId) {
        const response = await this.getViewsByStatusRaw(statusId);
        return response.data ?? [];
    }
    /**
     * Get order views by status (raw)
     */
    getViewsByStatusRaw(statusId) {
        const endpoint = this.getEndpoint('views-by-status', `status/${statusId}/view`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== PROCESSED/UNPROCESSED VIEWS ====================
    /**
     * Get processed order views
     * GET /api/orders/processed/view
     */
    async getProcessedViews() {
        const response = await this.getProcessedViewsRaw();
        return response.data ?? [];
    }
    /**
     * Get processed order views (raw)
     */
    getProcessedViewsRaw() {
        const endpoint = this.getEndpoint('views-processed', 'processed/view');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get unprocessed order views
     * GET /api/orders/unprocessed/view
     */
    async getUnprocessedViews() {
        const response = await this.getUnprocessedViewsRaw();
        return response.data ?? [];
    }
    /**
     * Get unprocessed order views (raw)
     */
    getUnprocessedViewsRaw() {
        const endpoint = this.getEndpoint('views-unprocessed', 'unprocessed/view');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
}
OrderModelViewService.DEFAULT_SERVICE_PATH = 'orders';
OrderModelViewService.BASE_PATH = 'api/orders';
//# sourceMappingURL=OrderModelViewService.js.map